// The homepage's one segment grid: a card per training page that exists,
// each opening that page. Uses the same artwork as each page's hero, so the
// card and the page it leads to read as one thing.
import { ArrowRight } from "lucide-react";
import { useT } from "@/shared/i18n/useT";
import { vars } from "@/shared/components/verticals/verticalBranches";
import { Eyebrow, Reveal } from "@/shared/pages/solutions/SolutionLanding";
import { SEGMENT_PATHS, type SegmentKey } from "@/shared/pages/solutions/segments";
import crisisArt from "@/shared/assets/images/bgcrisis2.jpg";
import ransomwareArt from "@/shared/assets/images/lockedlaptop.jpg";
import policeArt from "@/shared/assets/images/policestandoff.jpg";
import universitiesArt from "@/shared/assets/images/bguniversities.jpg";

const navigate = (path: string) => (window as any).navigate?.(path);
const K = "homeLanding.segments";

/** Artwork and its focal point (object-position) for each segment's card. */
const SEGMENTS: { key: SegmentKey; art: string; focus: string }[] = [
  { key: "crisis", art: crisisArt, focus: "center" },
  { key: "ransomware", art: ransomwareArt, focus: "25% 55%" },
  { key: "police", art: policeArt, focus: "55% 45%" },
  { key: "universities", art: universitiesArt, focus: "75% 30%" },
];

export function SegmentGrid() {
  const t = useT();
  return (
    <section
      id="segments"
      aria-labelledby="segments-title"
      className="scroll-mt-16 px-4 py-16 sm:px-6 sm:py-20"
    >
      <Reveal className="mx-auto max-w-[68rem]">
        <Eyebrow>{t(`${K}.eyebrow`)}</Eyebrow>
        <h2
          id="segments-title"
          className="ql-reveal font-home-serif font-medium text-home-navy"
          style={vars({
            fontSize: "clamp(1.75rem, 3.4vw, 2.5rem)",
            lineHeight: 1.12,
            "--reveal-delay": "60ms",
          })}
        >
          {t(`${K}.title`)}
        </h2>
        <ul className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {SEGMENTS.map(({ key, art, focus }, i) => {
            const href = SEGMENT_PATHS[key];
            return (
            <li
              key={key}
              className="ql-reveal"
              style={vars({ "--reveal-delay": `${120 + i * 70}ms` })}
            >
              <a
                href={href}
                onClick={(event) => {
                  if (event.metaKey || event.ctrlKey) return;
                  event.preventDefault();
                  navigate(href);
                }}
                className="group flex h-full flex-col overflow-hidden rounded-lg border border-home-line bg-home-paper transition-[border-color,box-shadow] hover:border-home-gold/60 hover:shadow-[0_24px_48px_-32px_rgba(12,26,51,0.45)] focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-home-navy"
              >
                <div className="relative aspect-[16/10] overflow-hidden bg-home-navy">
                  <img
                    src={art}
                    alt=""
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
                    style={{ objectPosition: focus }}
                  />
                  <div aria-hidden className="absolute inset-0 bg-home-navy/35" />
                </div>
                <div className="flex flex-1 flex-col p-5">
                  <h3 className="font-home-serif text-[1.25rem] font-medium leading-tight text-home-navy">
                    {t(`${K}.${key}Title`)}
                  </h3>
                  <p className="mt-2 flex-1 text-[0.875rem] leading-[1.55] text-home-muted">
                    {t(`${K}.${key}Body`)}
                  </p>
                  <span className="mt-4 inline-flex items-center gap-1.5 text-[0.875rem] font-medium text-home-gold-ink">
                    {t(`${K}.open`)}
                    <ArrowRight size={14} className="ql-arrow" aria-hidden />
                  </span>
                </div>
              </a>
            </li>
            );
          })}
        </ul>
      </Reveal>
    </section>
  );
}
